import { useCallback, useEffect, useMemo } from 'react';

import { useCookiesContext } from '@/Cookies';

import { DEFAULT_LOCALE, Locale, RIGHT_TO_LEFT_LOCALES } from './constants';

export const PREFERRED_LOCALE_COOKIE_NAME = 'preferredLocale';

export function getValidLocale(locale: string): Locale | null {
  if (locale.startsWith('en')) {
    return Locale.ENGLISH;
  }
  if (locale.startsWith('es')) {
    return Locale.SPANISH;
  }
  if (locale.startsWith('zh-CN')) {
    return Locale.CHINESE_SIMPLIFIED;
  }
  if (locale.startsWith('pt')) {
    return Locale.PORTUGUESE;
  }
  if (locale.startsWith('ht')) {
    return Locale.HAITIAN_CREOLE;
  }
  if (locale.startsWith('ko')) {
    return Locale.KOREAN;
  }
  if (locale.startsWith('ru')) {
    return Locale.RUSSIAN;
  }
  if (locale.startsWith('vi')) {
    return Locale.VIETNAMESE;
  }
  if (locale.startsWith('ar')) {
    return Locale.ARABIC;
  }
  if (locale.startsWith('fil')) {
    return Locale.FILIPINO;
  }
  if (locale.startsWith('zh-hant')) {
    return Locale.CHINESE_TRADITIONAL;
  }
  if (locale.startsWith('sm')) {
    return Locale.SAMOAN;
  }
  if (locale.startsWith('km')) {
    return Locale.KHMER;
  }
  if (locale.startsWith('pl')) {
    return Locale.POLISH;
  }
  if (locale.startsWith('uk')) {
    return Locale.UKRAINIAN;
  }
  if (locale.startsWith('ur')) {
    return Locale.URDU;
  }
  if (locale.startsWith('kea')) {
    return Locale.CAPE_VERDEAN_CREOLE;
  }
  if (locale.startsWith('fa')) {
    return Locale.FARSI;
  }
  if (locale.startsWith('fr')) {
    return Locale.FRENCH;
  }
  if (locale.startsWith('so')) {
    return Locale.SOMALI;
  }
  if (locale.startsWith('am')) {
    return Locale.AMHARIC;
  }
  if (locale.startsWith('mh')) {
    return Locale.MARSHALLESE;
  }
  if (locale.startsWith('hmn')) {
    return Locale.HMONG;
  }
  if (locale.startsWith('hi')) {
    return Locale.HINDI;
  }
  if (locale.startsWith('hu')) {
    return Locale.HUNGARIAN;
  }
  if (locale.startsWith('nl')) {
    return Locale.DUTCH;
  }
  return null;
}

export function setDocumentLangAttribute(validLocale: string): void {
  document.documentElement.setAttribute('lang', validLocale);
}

export function getCurrentLocaleFromCookies(cookies: any): Locale {
  const cookieValue = cookies[PREFERRED_LOCALE_COOKIE_NAME];
  return getValidLocale(cookieValue || navigator.language) || DEFAULT_LOCALE;
}

export function usePreferredLocale(): {
  locale: Locale;
  setLocale: (newLocale: Locale) => void;
  localeIsRightToLeft: boolean;
} {
  const { cookies, setCookie } = useCookiesContext();

  const locale = getCurrentLocaleFromCookies(cookies);

  /**
   * `useEffect` is often dangerous and unnecessary. This usage needs review by #ux-programs team.
   * See https://getcolor.atlassian.net/browse/UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    setDocumentLangAttribute(locale);
  }, [locale]);

  const setLocale = useCallback(
    (newLocale: Locale) => {
      setCookie(PREFERRED_LOCALE_COOKIE_NAME, newLocale, { path: '/' });
    },
    [setCookie]
  );

  const localeIsRightToLeft = useMemo(() => RIGHT_TO_LEFT_LOCALES.has(locale), [locale]);

  return {
    locale,
    setLocale,
    localeIsRightToLeft,
  };
}
